import React from "react";
import { Modal } from "react-bootstrap";
import ErrorAlert from "../../../common/ui/ErrorAlert.js";
import LocationLoader from "./LocationLoader.js";
import Location from "./Location.js";

/* =============================================================================
   Store picker.

   ⚠ backdrop="static" and keyboard={false} are load-bearing, not styling. On a
   first visit there is no store yet, and the whole menu depends on one being
   chosen — letting Escape or a backdrop click close this leaves the customer on
   an empty page with no way back. The close button appears only once
   currentLocation.LocationID is set, which is the same rule.

   ⚠ The open-only switch is hidden in delivery mode on purpose. In delivery the
   store is chosen for the customer by address, so filtering it is meaningless.

   ⚠ The back arrow only shows when the pickup/delivery chooser sent the customer
   here AND no store is picked yet — going "back" after choosing would strand
   them.

   Still react-bootstrap's Modal: it owns the backdrop, the focus trap and the
   body scroll lock. Only the chrome inside it is ours.
   ============================================================================= */

export default function LocationList(props) {

    const list = props.locationList.filtered || [];

    const locationsMapped = list.map((item) =>
        <Location
            key={item.locationNumber}
            data={item}
            handleSelectLocation={props.handleSelectLocation}
            currentLocation={props.currentLocation}
        />
    );

    const loading = [...Array(5)].map((item, index) => <LocationLoader key={index} />);

    const showBack = props.pickupOrDelivery.PickupDeliveryModal.ShowBackArrowPickupLocations
        && !props.currentLocation.LocationID;
    const isDelivery = props.pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'delivery';

    // Nothing matched. Without this the body is simply blank and the customer
    // has no idea whether it is still loading or their postcode has no store.
    const emptyState =
        <div className="wo-loc__empty">
            <p className="wo-loc__emptyHead">No stores found</p>
            <p className="wo-loc__emptySub">
                {props.locationList.showOpenOnly
                    ? 'Try turning off "Show open stores only", or search a different suburb.'
                    : 'Try a different suburb or postcode.'}
            </p>
        </div>;

    let body;
    if (props.locationLoading) {
        body = loading;
    } else if (props.locationError.HasError) {
        body = <div className="mt-3"><ErrorAlert message={props.locationError.ErrorMessage} /></div>;
    } else if (locationsMapped.length === 0) {
        body = emptyState;
    } else {
        body = locationsMapped;
    }

    return (
        <Modal
            show={props.show}
            backdrop="static"
            keyboard={false}
            centered
            scrollable
            dialogClassName="wo-locModal"
            contentClassName="wo-locModal__content"
        >
            <div className="wo-locModal__head">
                {showBack &&
                    <button type="button" className="wo-locModal__back"
                            onClick={props.handleBackPickupLocationsToMethodClick} aria-label="Back">
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>}

                <div className="wo-locModal__titles">
                    <h2 className="wo-locModal__title">Choose a store</h2>
                    <p className="wo-locModal__sub">
                        {props.currentLocation.LocationID
                            ? 'Changing store will empty your basket.'
                            : 'Pick where you would like to order from.'}
                    </p>
                </div>

                {props.currentLocation.LocationID &&
                    <button type="button" className="wo-locModal__close"
                            onClick={props.handleCloseLocModal} aria-label="Close">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                            <path d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    </button>}
            </div>

            <div className="wo-locModal__tools">
                <div className="wo-locSearch">
                    <span className="wo-locSearch__icon" aria-hidden="true" onClick={props.handleFocusSearchLocation}>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round">
                            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
                        </svg>
                    </span>
                    <input
                        id="txtPostcodeLocSearch"
                        ref={props.locationSearchInputRef}
                        className="wo-locSearch__input"
                        type="text"
                        placeholder="Suburb or postcode"
                        aria-label="Search by suburb or postcode"
                        value={props.locationList.searchFilterText}
                        onChange={props.handleSearchLocation}
                    />
                    {props.locationList.showClearButton &&
                        <button type="button" className="wo-locSearch__clear"
                                onClick={props.handleClearSearchLocation} aria-label="Clear search">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>}
                </div>

                <div className="wo-locModal__row">
                    <button type="button" className="wo-locModal__geo" onClick={props.getGeoLocation}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="3.2" />
                            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                        </svg>
                        Use my location
                    </button>

                    {!isDelivery &&
                        <label className="wo-toggle" htmlFor="switchShowOpenStores">
                            <input
                                type="checkbox"
                                id="switchShowOpenStores"
                                className="wo-toggle__input"
                                onChange={props.handleToggleOpenLocation}
                                checked={props.locationList.showOpenOnly}
                            />
                            <span className="wo-toggle__track" aria-hidden="true"><i></i></span>
                            <span className="wo-toggle__label">Open now only</span>
                        </label>}
                </div>
            </div>

            <Modal.Body className="wo-locModal__body">
                {props.menuError.HasError &&
                    <div className="mb-3"><ErrorAlert message={props.menuError.ErrorMessage} /></div>}
                {body}
            </Modal.Body>
        </Modal>
    )
}
